import { SelectionMode } from "./SelectionMode";
import { logger } from "../../logger/Logger";
import { warnOnce } from "../../application/LoggingUtils";

/* eslint-disable no-unused-vars */

/**
 * @readonly
 * @enum {number}
 * @alias NODE_TYPE
 * @property {number} NODE_TYPE_ASSEMBLY Real world object as assembly of sub-objects
 * @property {number} NODE_TYPE_INSERT Insert of multiple-instanced object
 * @property {number} NODE_TYPE_LAYER A layer (specific abstraction collection)
 * @property {number} NODE_TYPE_COLLECTION An abstract collection of objects (e.g. “Doors”)
 * @property {number} NODE_TYPE_COMPOSITE A real world object whose internal structure is not relevant to end user
 * @property {number} NODE_TYPE_MODEL Root of tree representing an entire Model. An aggregate model can contain multiple nested models.
 * @property {number} NODE_TYPE_GEOMETRY Leaf geometry node
 * @property {number} NODE_TYPE_BITS mask for all bits used by node type
 */
export const NODE_TYPE = {
  NODE_TYPE_ASSEMBLY: 0x0,
  NODE_TYPE_INSERT: 0x1,
  NODE_TYPE_LAYER: 0x2,
  NODE_TYPE_COLLECTION: 0x3,
  NODE_TYPE_COMPOSITE: 0x4,
  NODE_TYPE_MODEL: 0x5,
  NODE_TYPE_GEOMETRY: 0x6,
  NODE_TYPE_BITS: 0x7
};

var NODE_FLAG_NOSELECT = 0x20000000,
  NODE_FLAG_OFF = 0x40000000,
  NODE_FLAG_HIDE = 0x80000000,

  // Flags defined and used in LMV
  NODE_FLAG_LOCK_VISIBLE = 0x00001000,
  NODE_FLAG_LOCK_EXPLODE = 0x00002000,
  NODE_FLAG_LOCK_SELECT = 0x00004000;

/* eslint-enable no-unused-vars */

/**
 * @param nodeAccess
 * @param objectCount
 * @param maxDepth
 * @class
 * @memberof Autodesk.Viewing
 * @alias Autodesk.Viewing.ObjectTree
 */
export function ObjectTree(nodeAccess, objectCount, maxDepth) {

  this.nodeAccess = nodeAccess;
  this.maxDepth = maxDepth;
  this.objectCount = objectCount;
  this.numHidden = 0;
  this.numOff = 0;
  this.numVisibilityLocked = 0;

  // when not using precomputed nodeBoxes, instances are used for on-the-fly bbox computation
  this.instances = null;
}

ObjectTree.prototype.dtor = function () {
  this.nodeAccess.dtor();
  this.nodeAccess = null;

  this.instances = null;
};

ObjectTree.prototype.setFlagNode = function (dbId, flag, value) {

  var old = this.nodeAccess.getNodeFlags(dbId);

  // "!!" converts to bool
  if (!!(old & flag) == value)
  return false;

  if (value)
  this.nodeAccess.setNodeFlags(dbId, old | flag);else

  this.nodeAccess.setNodeFlags(dbId, old & ~flag);

  return true;
};

ObjectTree.prototype.setFlagGlobal = function (flag, value) {
  if (flag === NODE_FLAG_HIDE) {
    // Delegate to setAllNodesHidden for NODE_FLAG_HIDE to keep numHidden in sync.
    this.setAllNodesHidden(value);
  } else {
    // For other flags, operates directly on the storage. Note that counters like
    // numOff or numVisibilityLocked are not updated in that case.
    this.nodeAccess.setFlagAll(flag, value);
  }
};

/**
 * When a node is OFF, it is completely skipped for display purposes
 *
 * @param {number} dbId - database id
 * @param {boolean} value - Value to set
 */
ObjectTree.prototype.setNodeOff = function (dbId, value) {
  var res = this.setFlagNode(dbId, NODE_FLAG_OFF, value);
  if (res) {
    if (value)
    this.numOff++;else

    this.numOff--;
  }
  return res;
};

ObjectTree.prototype.isNodeOff = function (dbId) {
  return !!(this.nodeAccess.getNodeFlags(dbId) & NODE_FLAG_OFF);
};


/**
 * When a node is HIDDEN it will display in ghosted style
 * if display of hidden objects is on
 *
 * @param {number} dbId - database id
 * @param {boolean} value - Value to set
 */
ObjectTree.prototype.setNodeHidden = function (dbId, value) {
  var res = this.setFlagNode(dbId, NODE_FLAG_HIDE, value);
  if (res) {
    if (value)
    this.numHidden++;else

    this.numHidden--;
  }
  return res;
};

/**
 * Whether a node id is hidden.
 *
 * @param {number} dbId - The node's database id
 * @returns {boolean}
 * @alias Autodesk.Viewing.ObjectTree#isNodeHidden
 */
ObjectTree.prototype.isNodeHidden = function (dbId) {
  return !!(this.nodeAccess.getNodeFlags(dbId) & NODE_FLAG_HIDE);
};

/**
 * Bulk-sets the HIDE flag on all nodes. Faster than recursive enumNodeChildren
 * when operating on the entire tree (e.g., isolate or show-all on root).
 *
 * @param {boolean} hidden - whether all nodes should be hidden
 */
ObjectTree.prototype.setAllNodesHidden = function (hidden) {
  this.nodeAccess.setFlagAll(NODE_FLAG_HIDE, hidden);
  this.numHidden = hidden ? this.nodeAccess.numNodes : 0;
};

ObjectTree.prototype.lockNodeSelection = function (dbId, value) {
  return this.setFlagNode(dbId, NODE_FLAG_LOCK_SELECT, value);
};

ObjectTree.prototype.isNodeSelectionLocked = function (dbId) {
  return !!(this.nodeAccess.getNodeFlags(dbId) & NODE_FLAG_LOCK_SELECT);
};

/**
 * When a node's visibility is locked it can not be hidden
 *
 * @param {number} dbId - database id
 * @param {boolean} value - Value to set
 */
ObjectTree.prototype.lockNodeVisible = function (dbId, value) {
  var res = this.setFlagNode(dbId, NODE_FLAG_LOCK_VISIBLE, value);
  if (res) {
    if (value) {
      this.numVisibilityLocked++;
    } else {
      this.numVisibilityLocked--;
    }
  }
  return res;
};

/**
 * @returns {boolean} true if any nodes have locked visibility
 */
ObjectTree.prototype.hasLockedVisibility = function () {
  return this.numVisibilityLocked > 0;
};

/**
 * Whether a node id's visiblitly is locked.
 *
 * @param {number} dbId - The node's database id
 * @returns {boolean}
 * @alias Autodesk.Viewing.ObjectTree#isNodeVisibleLocked
 */
ObjectTree.prototype.isNodeVisibleLocked = function (dbId) {
  return !!(this.nodeAccess.getNodeFlags(dbId) & NODE_FLAG_LOCK_VISIBLE);
};

/**
 * Whether a node id's explode is locked.
 *
 * @param {number} dbId - The node's database id
 * @returns {boolean}
 * @alias Autodesk.Viewing.ObjectTree#isNodeExplodeLocked
 */
ObjectTree.prototype.isNodeExplodeLocked = function (dbId) {
  return !!(this.nodeAccess.getNodeFlags(dbId) & NODE_FLAG_LOCK_EXPLODE);
};

/**
 * When a node's explode is locked it won't explode
 *
 * @param {number} dbId - database id
 * @param {boolean} value - Value to set
 */
ObjectTree.prototype.lockNodeExplode = function (dbId, value) {
  return this.setFlagNode(dbId, NODE_FLAG_LOCK_EXPLODE, value);
};

/**
 * Gets the type associated with the node, such as assmebly, layer, model, geometry, etc.
 *
 * @param {number} dbId - The node's database id
 * @returns {number} one of {@link NODE_TYPE}
 * @alias Autodesk.Viewing.ObjectTree#getNodeType
 */
ObjectTree.prototype.getNodeType = function (dbId) {
  return this.nodeAccess.getNodeFlags(dbId) & NODE_TYPE.NODE_TYPE_BITS;
};

/**
 * Whether the node is a selectable entity.
 *
 * @param {number} dbId - The node's database id
 * @returns {boolean}
 * @alias Autodesk.Viewing.ObjectTree#isNodeSelectable
 */
ObjectTree.prototype.isNodeSelectable = function (dbId) {
  return !(this.nodeAccess.getNodeFlags(dbId) & NODE_FLAG_NOSELECT);
};

/**
 * Gets the database id of the node's parent.
 *
 * @param {number} dbId - The node's database id
 * @returns {number}
 * @alias Autodesk.Viewing.ObjectTree#getNodeParentId
 */
ObjectTree.prototype.getNodeParentId = function (dbId) {
  return this.nodeAccess.getParentId(dbId);
};

/**
 * Gets the nesting level of the given node.
 * Root node has level 0.
 *
 * @param {number} dbId
 * @returns {number}
 * @private
 */
ObjectTree.prototype.getNodeLevel = function (dbId) {
  return this.nodeAccess.getNodeLevel(dbId) ?? 1e10;
};

/**
 * Gets the model's root database id.
 *
 * @returns {number}
 * @alias Autodesk.Viewing.ObjectTree#getRootId
 */
ObjectTree.prototype.getRootId = function () {
  return this.nodeAccess.rootId;
};

/**
 * Gets the name associated to the id.
 *
 * @param {number} dbId - The node's database id
 * @param {boolean} includeCount - True if must include count
 * @returns {string}
 * @alias Autodesk.Viewing.ObjectTree#getNodeName
 */
ObjectTree.prototype.getNodeName = function (dbId, includeCount) {
  return this.nodeAccess.name(dbId, includeCount);
};

/**
 * Get number of children under the specified id.
 *
 * @param {number} dbId - The node's database id
 * @returns {number}
 * @alias Autodesk.Viewing.ObjectTree#getChildCount
 */
ObjectTree.prototype.getChildCount = function (dbId) {
  return this.nodeAccess.getNumChildren(dbId);
};

/**
 * Get number of instances under the specified id.
 *
 * @param {number} dbId - The node's database id
 * @returns {number}
 * @alias Autodesk.Viewing.ObjectTree#getInstanceCount
 */
ObjectTree.prototype.getInstanceCount = function (dbId) {
  return this.nodeAccess.getNumFragments(dbId);
};


var _tmpArray = new Array(6);

/**
 * Sets the bounding box values for a particular id on the 2nd argument provided.
 * There is no return value.
 *
 * @param {number} dbId - The node's database id
 * @param {Float32Array} dst - An array holding 6 number values: (min-x, min-y, min-z, max-x, max-y, max-z)
 *
 * @alias Autodesk.Viewing.ObjectTree#getNodeBox
 */
ObjectTree.prototype.getNodeBox = function (dbId, dst) {

  // If precomputed boxes are available, just return the box directly.
  if (this.nodeAccess.nodeBoxes) {
    this.nodeAccess.getNodeBox(dbId, dst);
    return;
  }

  // If instances are available, compute nodeBox recursively from instance boxes
  if (this.instances) {
    dst[0] = dst[1] = dst[2] = Infinity;
    dst[3] = dst[4] = dst[5] = -Infinity;
    this.enumNodeInstances(dbId, (instanceId) => {
      this.instances.getOriginalWorldBounds(instanceId, _tmpArray);
      dst[0] = Math.min(dst[0], _tmpArray[0]);
      dst[1] = Math.min(dst[1], _tmpArray[1]);
      dst[2] = Math.min(dst[2], _tmpArray[2]);
      dst[3] = Math.max(dst[3], _tmpArray[3]);
      dst[4] = Math.max(dst[4], _tmpArray[4]);
      dst[5] = Math.max(dst[5], _tmpArray[5]);
    }, true);
    return;
  }

  logger.error('getNodeBox() requires instance boxes or nodeBoxes');
};


ObjectTree.prototype.getNodeIndex = function (dbId) {
  return this.nodeAccess.getIndex(dbId);
};

/**
 * Callback function for {@link Autodesk.Viewing.ObjectTree#enumNodeInstances}
 *
 * @callback Autodesk.Viewing.ObjectTree~onEnumNodeInstances
 * @param {number} instanceId - The instance's id.
 */

/**
 *
 * @param {number} node - The id of a node.
 * @param {Autodesk.Viewing.ObjectTree~onEnumNodeInstances} callback - The function that will be called for each instance.
 * Note that if the callback function returns a truthy value, a loop over the instances and child nodes will be interrupted
 * and the callback result will be forwarded back to the caller.
 * @param {boolean} [recursive=false] - Whether the callback function gets called for child nodes, too.
 *
 * @alias Autodesk.Viewing.ObjectTree#enumNodeInstances
 */
ObjectTree.prototype.enumNodeInstances = function (node, callback, recursive) {

  //TODO: Temporary until we are consistently using dbId
  var dbId;
  if (typeof node == "number")
  dbId = node;else
  if (node)
  dbId = node.dbId;

  /**
   * @param {string} dbId - Database ID
   */
  const traverse = (dbId) => {
    var res = this.nodeAccess.enumNodeFragments(dbId, callback);

    if (res)
    return res;

    if (recursive) {
      res = this.enumNodeChildren(dbId, (child_dbId) => traverse(child_dbId));

      if (res)
      return res;
    }
  };

  return traverse(dbId);

};

/**
 * Callback function for {@link Autodesk.Viewing.ObjectTree#enumNodeChildren}
 *
 * @callback Autodesk.Viewing.ObjectTree~onEnumNodeChildren
 * @param {number} dbId - The id of a child node or, if `parentId` is not provided,
 * the id of the initial node
 * @param {number|undefined} parentId - The id of a parent node. If not provided, the `dbId` refers
 * to the initial node.
 */

/**
 *
 * @param {number} node - The id of a node.
 * @param {Autodesk.Viewing.ObjectTree~onEnumNodeChildren} callback - The function that will be called for each child node.
 * Note that if the callback function returns a truthy value, a loop over the child nodes will be interrupted
 * and the callback result will be forwarded back to the caller.
 * @param {boolean} [recursive=false] - Whether the callback function gets called for indirect child nodes, too.
 *
 * @alias Autodesk.Viewing.ObjectTree#enumNodeChildren
 */
ObjectTree.prototype.enumNodeChildren = function (node, callback, recursive) {

  //TODO: Temporary until we are consistently using dbId
  var dbId;
  if (typeof node == "number")
  dbId = node;else
  if (node)
  dbId = node.dbId;

  if (recursive) {
    if (callback(dbId))
    return dbId;
  }

  /**
   * @param {string} dbId - Database ID
   */
  const traverse = (dbId) => {
    var res = this.nodeAccess.enumNodeChildren(dbId, (childId) => {
      // TODO: make this logic more explicit for consumers of this function
      // or (ideally) get rid of it i.e. a refactoring required.
      // Don't forget to refactor `enumNodeInstances` as well.
      if (callback(childId, dbId))
      return childId;

      if (recursive)
      return traverse(childId);
    });

    if (res)
    return res;
  };

  return traverse(dbId);
};


//Given a leaf node, find the correct parent
//node to select according to the given selection mode
ObjectTree.prototype.findNodeForSelection = function (dbId, selectionMode) {

  //Default legacy mode -- select exactly the node we got asked for.
  if (selectionMode === SelectionMode.LEAF_OBJECT)
  return dbId;

  var res = dbId;
  var node, nt;

  if (selectionMode === SelectionMode.FIRST_OBJECT) {
    //1. Find the leaf node of the selection tree containing it and then follow the chain of parents all the way up to the root to get the complete path from root to leaf node.
    //2. Start at the root and walk down the path until the first node that is not a Model, Layer or Collection. Select it.
    var idpath = [];

    node = dbId;
    while (node) {
      idpath.push(node);
      node = this.getNodeParentId(node);
    }

    for (var i = idpath.length - 1; i >= 0; i--) {
      nt = this.getNodeType(idpath[i]);
      if (nt !== NODE_TYPE.NODE_TYPE_MODEL &&
      nt !== NODE_TYPE.NODE_TYPE_LAYER &&
      nt !== NODE_TYPE.NODE_TYPE_COLLECTION) {
        res = idpath[i];
        break;
      }
    }
  } else

  if (selectionMode === SelectionMode.LAST_OBJECT) {
    // Start at the leaf and walk up the path until the first node that is Composite. Select it. If there’s no Composite node in the path select the leaf.

    node = dbId;
    while (node) {
      nt = this.getNodeType(node);
      if (nt === NODE_TYPE.NODE_TYPE_COMPOSITE) {
        res = node;
        break;
      }
      node = this.getNodeParentId(node);
    }

  }

  return res;

};

// When not using precomputed bboxes, the instance boxes are needed for on-the-fly computation of node boxes.
ObjectTree.prototype.getInstances = function () {
  return this.instances;
};

ObjectTree.prototype.setInstances = function (instances) {
  this.instances = instances;
};

/**
 * Search the tree for nodes whose names match the given string.
 *
 * @param {string} text - The search term (not case sensitive).
 * @returns {number[]} - The dbIds of all nodes in the tree matching the search text.
 *
 * @alias Autodesk.Viewing.ObjectTree#search
 */
ObjectTree.prototype.search = function search(text) {
  const searchText = text.toLowerCase();

  let matchingNodeIds = [];
  this.enumNodeChildren(this.getRootId(), (dbId, parentId) => {
    const nodeName = this.getNodeName(dbId)?.toLowerCase() || '';
    if (nodeName.indexOf(searchText) > -1) {
      matchingNodeIds.push(dbId);
    }

  }, /*recursive=*/true);

  return matchingNodeIds;
};

// DEPRECATED InstanceTree class and fragment-named aliases. Remove with V8

ObjectTree.prototype.getFragmentCount = function (dbId) {
  return this.getInstanceCount(dbId);
};

ObjectTree.prototype.enumNodeFragments = function (node, callback, recursive) {
  return this.enumNodeInstances(node, callback, recursive);
};

ObjectTree.prototype.setFragmentList = function (instances) {
  return this.setInstances(instances);
};

Object.defineProperty(ObjectTree.prototype, 'fragList', {
  get: function () {
    warnOnce("[ObjectTree.fragList] is deprecated and will be removed in a future release, use ObjectTree#getInstances instead.");
    return this.instances;
  }
});

/**
 * See {@link Autodesk.Viewing.ObjectTree} for the current object tree API.
 *
 * @class
 * @deprecated Use {@link Autodesk.Viewing.ObjectTree} instead.
 * @memberof Autodesk.Viewing.Private
 * @alias Autodesk.Viewing.Private.InstanceTree
 */
export const InstanceTree = ObjectTree;